<template lang="html">
  <div id="root">
    <b-container fluid>
      <b-row>
        <b-form-checkbox-group stacked v-model="otherAreas" :options="otherAreasList" style="
          position: absolute;
          top: 150px;
          left: 10px;
          background: #fff;
          padding: 20px;
          height: auto;
          max-height: 430px;
          overflow-y: scroll;
          z-index: 1000">
        </b-form-checkbox-group>
        <editable-map
          editable
          ref="map"
          id="map"
          :zoom="12"
          :center="center"
          style="width: 100%; height: 500px"
          @ready="emitReady"
        >
          <l-tile-layer
            v-for="tileProvider in tileProviders"
            :key="tileProvider.name"
            :name="tileProvider.name"
            :visible="tileProvider.visible"
            :url="tileProvider.url"
            :attribution="tileProvider.attribution"
            layer-type="base"
            :options="tileProvider.options"
            v-if="tileProvidersReady"
          />
          <l-control-fullscreen position="bottomleft"
            :options="{title: { false: $gettext('Go fullscreen'), true: $gettext('Back to normal') }}"
          />
          <l-control-layers
            :position="layersPosition"
            :collapsed="false"
            :sort-layers="false"
          />
          <div v-if="paths.length > 0">
            <l-control position="topright" v-for="(path, i) in paths" :key="i">
              <div class="leaflet-bar">
                <a href="#" @click.prevent="removePath(i);" style="position: relative;">▱ {{i+1}}<span style="position: absolute;left: 50%; top:50%; transform: translate(-.7rem, -1rem);font-size: 1.3rem" class="text-danger">×&nbsp;&nbsp;</span></a>
              </div>
            </l-control>
            <l-control position="topright">
              <div class="leaflet-bar">
                <a href="#" @click.prevent="addPath()" style="position: relative;">▱ +</a>
              </div>
            </l-control>
          </div>
          <div v-else>
            <l-control position="topright">
              <div class="leaflet-bar">
                <a href="#" @click.prevent="addPath()" style="position: relative;">▱ +</a>
              </div>
            </l-control>
          </div>
          <editable-polygon
            ref="polygon"
            v-if="paths.length > 0"
            :editable="editablePolygon"
            :lat-lngs="paths"
            :color="area.is_dangerous ? 'red' : '#3388ff'"
            :fillColor="area.is_dangerous ? 'red' : '#3388ff'"
            @editable:editing="updateEdited($event)"
          />
          <editable-polygon
            ref="otherPolygon"
            v-if="otherAreas.length > 0"
            :editable="false"
            :lat-lngs="visibleOtherAreas"
          />
        </editable-map>
      </b-row>
      <b-row>
        <b-col cols="12" class="text-center mt-3">
          <b-button v-b-modal.modalEnterGeoJson v-translate>Copy/Paste Standard GeoJSON</b-button>
        </b-col>
      </b-row>
    </b-container>
    <br>
    <b-container>
      <b-row>
        <b-col>
          <b-alert :show="dismissCountDown" dismissible variant="success" @dismissed="dismissCountDown=0" @dismiss-count-down="countDownChanged">
            <h5 v-translate>Area was updated!</h5>
          </b-alert>
          <b-card>
            <h1 :class="area.is_dangerous ? 'text-danger' : ''">{{area.name}} <small>({{area.country}})</small></h1>
            <b-form>
              <b-form-row>
                <div class="form-group col-12">
                  <label for="change-status" v-translate>Name</label>
                  <div class="input-group">
                    <b-form-input v-model="area.name" type="text" />
                  </div>
                </div>
                <b-col cols="12">
                  <b-form-group>
                    <b-form-checkbox variant="danger" size="lg" v-model="area.is_dangerous" :value="1" :unchecked-value="0" switch>
                      <span :class="area.is_dangerous ? 'text-danger' : ''" v-translate>It's a dangerous area</span>
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
                <div class="form-group col-12">
                  <label for="change-status" v-translate>Country (2 letter ISO code)</label>
                  <div class="input-group">
                    <b-form-input v-model="area.country" type="text" />
                  </div>
                </div>
                <div class="form-group col-12">
                  <label for="change-status" v-translate>Language Locale (ISO 15897 code, ie. <em>es_AR</em> or <em>pt_BR</em>)</label>
                  <div class="input-group">
                    <b-form-input v-model="area.locale" type="text" />
                  </div>
                </div>
                <div class="form-group col-12">
                  <label for="change-status" v-translate>Currency (3 letter ISO code)</label>
                  <div class="input-group">
                    <b-form-input v-model="area.currency" type="text" />
                  </div>
                </div>
                <div class="form-group col-12">
                  <label for="change-status" v-translate>Currency symbol</label>
                  <div class="input-group">
                    <b-form-input v-model="area.symbol" type="text" />
                  </div>
                </div>
              </b-form-row>

              <div class="form-group col-12 form-actions">
                <label class="col-md-3 control-label"></label>
                <div class="col-md-8">
                  <b-button variant="secondary" size="lg" @click.prevent="updateArea()" v-translate>Save Changes</b-button>
                </div>
              </div>
            </b-form>
          </b-card>
        </b-col>
        <b-col>
          <b-card>
            <h2 v-translate>Platform Area Mapping</h2>
            <label for="meli-area">MELI Area ID</label>
            <b-form-input id="meli-area" v-if="platformAreaMapping.meli" v-model="platformAreaMapping.meli.external_id" type="text"></b-form-input>
            <div class="form-actions mt-3">
              <b-button size="sm" variant="secondary" @click.prevent="updatePlatformAreaMappings" v-translate>
                Update Mapping
              </b-button>
            </div>
          </b-card>
          <b-card class="area-formulas" v-if="shippingSizes">
            <h2 v-translate>Quotes</h2>
            <b-table striped :items="areaFormulas" :fields="areaFormulasFields" v-if="areaFormulas.length > 0">
              <template v-slot:cell(size_id)="data">
                {{ shippingSizes.findIndex(x => x.id === data.item.size_id) !== -1 ? shippingSizes.find(x => x.id === data.item.size_id).name : $gettext('All') }}
              </template>
              <template v-slot:cell(actions)="data">
                <b-button size="sm" v-b-modal.modalEditAreaQuote @click="editingAreaFormula = data.item" style="padding: .5rem .55rem">
                  <font-awesome-icon :icon="['fa', 'pencil-alt']" size="2x" style="font-size: 16px;vertical-align: middle" />
                </b-button>
              </template>
            </b-table>
            <b-button v-b-modal.modalAddQuote @click="editingAreaFormula = {area_id: area.id}" v-translate>Add Quote</b-button>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
    <b-modal id="modalAddQuote" ref="modalAddQuote" size="lg" :hide-footer="true" @hidden="resetEditingAreaFormula">
      <div slot="modal-title" v-translate>Add New Area Quote</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <quotes-create :baseAreaFrom="area" :isModal="true" @quotecreated="quoteCreated" @cancel="quoteCreated" />
    </b-modal>
    <b-modal id="modalEnterGeoJson" ref="modalEnterGeoJson" @show="editablePolygon = false" @hide="editablePolygon = true" :hide-footer="true">
      <div slot="modal-title" v-translate>Paste a Standard GeoJSON</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <b-form-row>
        <div class="form-group col-12">
          <label class="col-md-3 control-label">GeoJSON</label>
          <b-textarea v-model="polygonGeojson" style="width: 100%; height: 200px"
            @input="readGeojson">
          </b-textarea>
        </div>
        <div v-if="errorMessage">{{errorMessage}}</div>
      </b-form-row>
        <b-form-row>
          <div class="text-center form-group col-12 form-actions">
            <b-button type="button" variant="outline-primary" size="lg" @click.prevent="$refs.modalEnterGeoJson.hide()" v-translate>Close</b-button>
          </div>
        </b-form-row>
    </b-modal>
    <b-modal id="modalEditAreaQuote" ref="modalEditAreaQuote" size="lg" :hide-footer="true">
      <div slot="modal-title" v-translate>Edit Area Quote</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <quotes-create
        :quote-data="{quotes: editingAreaFormula}"
        :base-area-from="area"
        @quoteupdated="quoteUpdated"
        @cancel="quoteUpdated"
        :isModal="true"
      />
    </b-modal>
  </div>
</template>

<script>
import { latLngBounds } from 'leaflet'
import {
  LTileLayer,
  LControlLayers,
  LControl
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import { EditableMap, EditablePolygon } from 'vue2-leaflet-editable'
import { SearchControl, OpenStreetMapProvider } from 'leaflet-geosearch'
import 'leaflet-geosearch/assets/css/leaflet.css'
import LControlFullscreen from 'vue2-leaflet-fullscreen'
import QuotesCreate from '@/components/QuotesCreate'

export default {
  components: {
    QuotesCreate,
    LTileLayer,
    LControlLayers,
    LControl,
    EditableMap,
    EditablePolygon,
    LControlFullscreen
  },
  data() {
    return {
      dismissSecs: 10,
      dismissCountDown: 0,
      area: {},
      areaFormulas: [],
      // areaFormulasFields: ['size_type', 'formula', {key: 'courier_commission_formula', label: 'Commission'}, 'actions'],
      areaFormulasFields: [{key: 'name', label: this.$gettext('Name')}, {key: 'size_id', label: this.$gettext('Size')}, {key: 'actions', label: this.$gettext('Actions')}],
      editingAreaFormula: {},
      edited: null,
      paths: [],
      mvcPaths: null,
      errorMessage: null,
      polygonGeojson: '',
      areaUpdated: false,
      otherAreas: [],
      otherAreasList: [],
      visibleOtherAreas: [],
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      opacity: 1,
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      currentZoom: 11,
      minZoom: 1,
      maxZoom: 18,
      zoomPosition: 'topleft',
      layersPosition: 'topright',
      attributionPosition: 'bottomright',
      imperial: false,
      editablePolygon: false,
      platformAreaMappings: [
        'meli'
      ],
      platformAreaMapping: {}
    }
  },
  watch: {
    otherAreas(val) {
      this.visibleOtherAreas = []
      for (let i = 0; i < val.length; i++) {
        this.visibleOtherAreas.push(val[i].coords[0])
      }
    },
    areasWithCoords(val) {
      console.log('AREAS WITH COORDS')
      this.otherAreasList = this.areasWithCoords.filter(x => x.id !== this.area.id)
    }
  },
  created() {
    this.fetchAreas()
    this.checkMapProviders()
  },
  mounted() {
    this.fetchPlatforms()
    this.fetchArea()
  },
  methods: {
    editAreaFormula() {
      delete this.editingAreaFormula.area
      delete this.editingAreaFormula.size_type
      delete this.editingAreaFormula.sort
      this.$http.patch(this.$appConfig.api.baseUrl + 'area_formulas/' + this.editingAreaFormula.id, this.editingAreaFormula)
        .then(response => {
          this.resetEditingAreaFormula()
          this.fetchArea()
        })
    },
    addAreaFormula() {
      delete this.editingAreaFormula.area
      delete this.editingAreaFormula.size_type
      delete this.editingAreaFormula.sort
      this.$http.post(this.$appConfig.api.baseUrl + 'area_formulas/', this.editingAreaFormula)
        .then(response => {
          this.resetEditingAreaFormula()
          this.fetchArea()
        })
    },
    fetchArea() {
      if (this.map) {
        this.$nextTick(() => {
          this.$http.get(this.$appConfig.api.baseUrl + 'areas/' + this.$route.params.id + '?includes[]=size_types&includes[]=area_formula&includes[]=platform_areas.platform')
            .then(response => {
              this.area = response.body

              this.readGeojson(JSON.stringify(this.area.area))

              this.center = latLngBounds(this.paths.map(o => o.map(x => [x.lat, x.lng]))).getCenter()
              this.fitted = true

              this.platformAreaMappings.forEach(slug => {
                console.log(this.area.platform_areas.findIndex(x => x.platform.slug === slug))
                if (this.area.platform_areas.findIndex(x => x.platform.slug === slug) !== -1) {
                  this.platformAreaMapping[slug] = this.area.platform_areas.find(x => x.platform.slug === slug)
                } else {
                  this.platformAreaMapping[slug] = {
                    area_id: this.area.id,
                    external_id: '',
                    platform_id: this.platforms.find(x => x.slug === slug).id
                  }
                }
              })

              // this.platformAreaMapping = this.platformAreaMappings.map(slug => {
              //   if (this.area.platform_areas.findIndex(x => x.platform.slug === slug)) {
              //     return this.area.platform_areas.find(x => x.platform.slug === slug)
              //   }
              // })

              console.log(this.platformAreaMapping)
              this.fetchAreaQuotes()
            })
        })
      } else {
        setTimeout(function () {
          this.fetchArea()
        }.bind(this), 100)
      }
    },
    updateArea() {
      if (this.areaUpdated === false) {
        delete this.area.area
      }
      this.$http.patch(this.$appConfig.api.baseUrl + 'areas/' + this.$route.params.id, this.area).then(response => {
        this.showAlert()
        this.fetchArea()
      }, error => {
        let obj = {
          title: this.$gettext('ERROR'),
          message: error.body.message,
          type: 'error'
        }
        this.$parent.$refs.simplert.openSimplert(obj)
      })
    },
    updatePlatformAreaMappings() {
      for (const mapping of Object.values(this.platformAreaMapping)) {
        if (mapping.id) {
          if (mapping.external_id !== '') {
            console.log('update')
            this.$http.patch(this.$appConfig.api.baseUrl + 'platform-areas/' + mapping.id, mapping)
              .then(response => {
                this.showAlert()
                this.fetchArea()
              }, error => {
                let obj = {
                  title: this.$gettext('ERROR'),
                  message: error.body.message,
                  type: 'error'
                }
                this.$parent.$refs.simplert.openSimplert(obj)
              })
          } else {
            console.log('delete')
            this.$http.delete(this.$appConfig.api.baseUrl + 'platform-areas/' + mapping.id)
              .then(response => {
                this.showAlert()
                this.fetchArea()
              }, error => {
                let obj = {
                  title: this.$gettext('ERROR'),
                  message: error.body.message,
                  type: 'error'
                }
                this.$parent.$refs.simplert.openSimplert(obj)
              })
          }
        } else {
          if (mapping.external_id !== '') {
            console.log('create')
            this.$http.post(this.$appConfig.api.baseUrl + 'platform-areas/', mapping)
              .then(response => {
                this.showAlert()
                this.fetchArea()
              }, error => {
                let obj = {
                  title: this.$gettext('ERROR'),
                  message: error.body.message,
                  type: 'error'
                }
                this.$parent.$refs.simplert.openSimplert(obj)
              })
          }
        }
      }
    },
    updateEdited($event) {
      this.editablePolygon = false
      this.areaUpdated = true

      let latLngs = $event.layer.getLatLngs()
      this.area.area = []
      this.area.area = latLngs.map(p => { return p.map(ll => { return ll.lat + ',' + ll.lng }).join('\n') })

      this.polygonGeojson = JSON.stringify({
        type: 'Polygon',
        coordinates: latLngs.map(p => { return this.closeLoop(p.map(ll => { return [ll.lng, ll.lat] })) })
      })

      this.editablePolygon = true
    },
    addPath () {
      this.areaUpdated = true
      this.editablePolygon = false
      // obtain the bounds, so we can guess how big the polygon should be
      let bounds = this.map.getBounds()
      let northEast = bounds.getNorthEast()
      let southWest = bounds.getSouthWest()
      let center = bounds.getCenter()
      let degree = this.paths.length + 1
      let f = Math.pow(0.66, degree)

      // Draw a triangle. Use f to control the size of the triangle.
      // i.e., every time we add a path, we reduce the size of the triangle
      let path = [
        { lng: center.lng, lat: (1 - f) * center.lat + (f) * northEast.lat },
        { lng: (1 - f) * center.lng + (f) * southWest.lng, lat: (1 - f) * center.lat + (f) * southWest.lat },
        { lng: (1 - f) * center.lng + (f) * northEast.lng, lat: (1 - f) * center.lat + (f) * southWest.lat }
      ]

      this.paths.push(path)
      this.area.area = this.paths.map(p => { return p.map(ll => { return ll.lat + ',' + ll.lng }).join('\n') })
      this.polygonGeojson = JSON.stringify({
        type: 'Polygon',
        coordinates: this.paths.map(p => { return this.closeLoop(p.map(ll => { return [ll.lng, ll.lat] })) })
      })
      this.$nextTick(() => {
        this.editablePolygon = true
      })
    },
    removePath(i) {
      this.areaUpdated = true
      this.editablePolygon = false
      this.paths.splice(i, 1)
      this.area.area = this.paths.map(p => { return p.map(ll => { return ll.lat + ',' + ll.lng }).join('\n') })
      this.polygonGeojson = JSON.stringify({
        type: 'Polygon',
        coordinates: this.paths.map(p => { return this.closeLoop(p.map(ll => { return [ll.lng, ll.lat] })) })
      })

      this.$nextTick(() => {
        this.editablePolygon = true
      })
    },
    readGeojson(geoJson) {
      try {
        this.paths = []
        this.editablePolygon = false
        this.polygonGeojson = geoJson

        let v = JSON.parse(geoJson)

        this.paths = v.coordinates.map(linearRing =>
          linearRing.slice(0, linearRing.length - 1)
            .map(([lng, lat]) => ({lat, lng}))
        )

        this.center = latLngBounds(this.paths.map(o => o.map(x => [x.lat, x.lng]))).getCenter()
        this.fitted = true

        this.$nextTick(() => {
          this.editablePolygon = true
        })
        this.errorMessage = null
      } catch (err) {
        this.errorMessage = err.message
      }
    },
    closeLoop (path) {
      return path.concat(path.slice(0, 1))
    },
    emitReady() {
      this.map = this.$refs.map.mapObject
      this.map.invalidateSize()
      this.map.on('popupclose', () => this.emitCourier(null))
      this.mapReady = true
      this.$emit('ready', true)
      this.editablePolygon = true

      // geosearch control
      const searchControl = new SearchControl({
        style: 'bar',
        provider: new OpenStreetMapProvider(),
        position: 'topright',
        searchLabel: this.$gettext('Enter city')
      })
      this.map.addControl(searchControl)
    },
    resetEditingAreaFormula() {
      // console.log(this.$data.editingAreaFormula)
      this.$refs.modalEditAreaFormula.hide()
      this.$refs.modalAddAreaFormula.hide()
      // Object.assign(this.$data.editingAreaFormula, this.$options.data.call(this).editingAreaFormula)
      this.editingAreaFormula = this.$options.data.call(this).editingAreaFormula
      // console.log(this.editingAreaFormula)
    },
    fetchAreaQuotes() {
      let params = {
        filter_groups: [{
          filters: [
            ['area_id_from', 'eq', this.area.id]
          ]
        }]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'quotes', {params: params})
        .then(response => {
          console.log(response.body)
          this.areaFormulas = response.body.data
          this.fetchShippingSizes()
        })
    },
    quoteCreated() {
      this.$refs.modalAddQuote.hide()
      this.fetchAreaQuotes()
    },
    quoteUpdated() {
      this.$refs.modalEditAreaQuote.hide()
      this.fetchAreaQuotes()
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    }
  }
}
</script>
